/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Chip, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Autocomplete, AutocompleteGetTagProps } from '@material-ui/lab';
import Name from 'components/Name/Name';
import React from 'react';
import { FieldRenderProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { IAutocompleteField } from 'types/shared';
import { CustomTypography, InputDiv } from '../Input.styled';
import CustomSelectInput from './CustomSelectInput';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface IProps extends FieldRenderProps<any> {
  options: Array<IAutocompleteField>;
  onChange?: (event: React.ChangeEvent<{}>, value: IAutocompleteField | Array<IAutocompleteField> | null) => void;
  multi?: boolean;
  label?: string;
  noOptionsText?: string;
  error?: string;
  spinner?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

type PropType = IProps;

const CustomSelect = ({
  onChange,
  input,
  meta,
  options,
  multi,
  label,
  placeholder,
  spinner,
  disabled,
  error,
  noOptionsText,
  disableClearable,
}: PropType): JSX.Element => {
  const { t } = useTranslation();

  const handleChange = (
    event: React.ChangeEvent<{}>,
    value: IAutocompleteField | Array<IAutocompleteField> | null,
  ): void => {
    if (onChange) {
      onChange(event, value);
    }
    input.onChange(value);
  };

  const renderTags = (
    value: {
      label: string;
      value: string;
    }[],
    getTagProps: AutocompleteGetTagProps,
  ): JSX.Element => {
    return (
      <React.Fragment>
        {value.map((tag, index) => (
          <Chip size="small" key="index" {...getTagProps({ index })} deleteIcon={<CloseIcon />} label={tag.label} />
        ))}
      </React.Fragment>
    );
  };

  return (
    <InputDiv>
      {label && <CustomTypography variant="subtitle2">{t(label)}</CustomTypography>}
      <Autocomplete
        disabled={disabled || spinner}
        multiple={multi}
        options={options}
        disableClearable={disableClearable}
        noOptionsText={!options.length ? (noOptionsText ? t(noOptionsText) : noOptionsText) : undefined}
        getOptionLabel={(option): string => (option.label ? option.label : '')}
        openText=""
        closeText=""
        clearText=""
        onChange={handleChange}
        getOptionSelected={(option, value): boolean => value.label === option.label || !multi}
        value={input.value ? input.value : []}
        renderInput={(parameters): JSX.Element => (
          <CustomSelectInput
            parameters={parameters}
            meta={meta}
            error={error}
            spinner={spinner}
            placeholder={placeholder && input.value?.length <= 0 ? t(placeholder) : undefined}
          />
        )}
        renderOption={(option): JSX.Element => <Name text={option.label} width={312} maxLines={1} />}
        renderTags={renderTags}
        size="small"
      />
      {meta.error && meta.touched && (
        <Typography variant="caption" color="error">
          {t(meta.error)}
        </Typography>
      )}
    </InputDiv>
  );
};

export default CustomSelect;
